import { api } from './api';
import type { AxiosError } from 'axios';

export interface AuthUser {
  id: string;
  email: string | null;
  name: string | null;
  pictureUrl: string | null;
  emailVerified: boolean;
  countryCode: string | null;
  primaryLocale: string | null;
}

interface AuthSession { user: AuthUser }
export type LoginResult = AuthSession | { mfaRequired: true };

export type ReportKind = 'dashboard' | 'variance' | 'pivot' | 'management_pl' | 'board_pack' | 'kpi';
export interface ReportAccessRow { id: string; report_kind: ReportKind; user_id: string; can_view: boolean }

export const authApi = {
  async register(dto: { email: string; name: string; password: string }): Promise<AuthSession & { verificationSent: boolean }> {
    const r = await api.post<AuthSession & { verificationSent: boolean }>('/api/auth/register', dto);
    return r.data;
  },

  async login(dto: { email: string; password: string }): Promise<LoginResult> {
    const r = await api.post<LoginResult>('/api/auth/login', dto);
    return r.data;
  },

  // ── MFA ──────────────────────────────────────────────────────────────────
  async verifyMfa(body: { code?: string; recoveryCode?: string }): Promise<AuthSession> {
    return (await api.post<AuthSession>('/api/auth/mfa/verify', body)).data;
  },
  async enrollMfa(): Promise<{ otpauthUrl: string; qrDataUrl: string }> {
    return (await api.post<{ otpauthUrl: string; qrDataUrl: string }>('/api/auth/mfa/enroll', {})).data;
  },
  async confirmMfa(code: string): Promise<{ recoveryCodes: string[] }> {
    return (await api.post<{ recoveryCodes: string[] }>('/api/auth/mfa/confirm', { code })).data;
  },
  async disableMfa(currentPassword: string): Promise<{ ok: true }> {
    return (await api.post<{ ok: true }>('/api/auth/mfa/disable', { currentPassword })).data;
  },

  // ── Report access (admin) ─────────────────────────────────────────────────
  async listReportAccess(wsId: string): Promise<ReportAccessRow[]> {
    return (await api.get<ReportAccessRow[]>(`/api/workspaces/${wsId}/report-access`)).data;
  },
  async putReportAccess(wsId: string, body: { reportKind: ReportKind; userId: string; canView: boolean }): Promise<ReportAccessRow> {
    return (await api.put<ReportAccessRow>(`/api/workspaces/${wsId}/report-access`, body)).data;
  },
  async deleteReportAccess(wsId: string, id: string): Promise<void> {
    await api.delete(`/api/workspaces/${wsId}/report-access/${id}`);
  },

  async forgotPassword(email: string): Promise<{ ok: true }> {
    const r = await api.post<{ ok: true }>('/api/auth/forgot-password', { email });
    return r.data;
  },

  async resetPassword(token: string, password: string): Promise<{ ok: true }> {
    const r = await api.post<{ ok: true }>('/api/auth/reset-password', { token, password });
    return r.data;
  },

  async verifyEmail(token: string): Promise<AuthSession> {
    const r = await api.post<AuthSession>('/api/auth/verify-email', { token });
    return r.data;
  },

  async resendVerification(email: string): Promise<{ ok: true }> {
    const r = await api.post<{ ok: true }>('/api/auth/resend-verification', { email });
    return r.data;
  },

  async logout(): Promise<void> {
    await api.post('/api/auth/logout');
  },

  /**
   * Build the OAuth start URL. Returns an absolute URL because the
   * OAuth dance hops cross-origin to the IdP and back.
   */
  oauthHref(provider: 'google' | 'apple'): string {
    const base = (import.meta.env.VITE_API_URL as string | undefined) || '';
    return `${base}/auth/${provider}`;
  },
};

/**
 * Read a friendly error message out of an axios error. Falls back to a
 * generic copy when the server returned no body or no message.
 */
export function readApiError(err: unknown, fallback = 'Something went wrong'): string {
  const e = err as AxiosError<{ message?: string | string[] }> | undefined;
  const msg = e?.response?.data?.message;
  if (Array.isArray(msg)) return msg[0] || fallback;
  if (typeof msg === 'string' && msg) return msg;
  return fallback;
}
